import React, { ReactNode } from 'react';
import { RhfMuiTextArea, RhfMuiTextField } from 'src/presentations/components/molecules/fields/rhf-mui-fields/RhfMuiSimple';
import { RhfMuiSelect } from 'src/presentations/components/molecules/fields/rhf-mui-fields/RhfMuiSelect';
import { RhfMuiRadioGroup } from 'src/presentations/components/molecules/fields/rhf-mui-fields/RhfMuiRadioGroup';
import { RhfMuiDatePicker } from 'src/presentations/components/molecules/fields/rhf-mui-fields/RhfMuiDatePicker';
import { RhfMuiDateTimePicker } from 'src/presentations/components/molecules/fields/rhf-mui-fields/RhfMuiDateTimePicker';
import { RhfMuiDateRangePicker } from 'src/presentations/components/molecules/fields/rhf-mui-fields/RhfMuiDateRangePicker';
import { RhfFieldSelectSubChargeDomain } from 'src/presentations/components/molecules/fields/custom-fields/RhfFieldSelectSubChargeDomain';
import { LegAndDomainAssignment } from 'src/presentations/components/organisms/forms/LegAndDomainAssignment';
import { ActionDetailNotice } from 'src/presentations/components/organisms/actionDetails/Notice';
import { FieldDefinition } from '../hooks/useQuestionnaireFields';

interface QuestionFieldProps {
  fieldDefinition: FieldDefinition;
  disabled?: boolean;
  deviationId?: string; // Add deviationId prop
  setupCustomInputs?: (noticeEl: ReactNode, inputsEl: ReactNode, disabled?: boolean) => ReactNode;
}

/**
 * Component that renders the appropriate field based on field definition
 */
export const QuestionField: React.FC<QuestionFieldProps> = ({ fieldDefinition, disabled, deviationId, setupCustomInputs }) => {
  if (!fieldDefinition) {
    return null;
  }

  const { type, name, label, props, domainType } = fieldDefinition;

  // Special handling for DO_YOU_ACCEPT_THIS_NEW_CHARGE_AS_IT question type
  const isNewCharge = domainType === 'DO_YOU_ACCEPT_THIS_NEW_CHARGE_AS_IT';

  // Special handling for DO_YOU_ACCEPT_THIS_DOUBLE_CHARGE question type
  // The backend sends DO_YOU_ACCEPT_THIS_CHARGE_AS_IS for the double charge question (new "Resolve all" option)
  const isDoubleCharge = domainType === 'DO_YOU_ACCEPT_THIS_DOUBLE_CHARGE' || domainType === 'DO_YOU_ACCEPT_THIS_CHARGE_AS_IS';

  // Render the base field component
  const renderBaseField = () => {
    switch (type) {
      case 'text':
        return <RhfMuiTextField name={name} label={label} {...props} disabled={disabled} />;

      case 'textarea':
        return <RhfMuiTextArea name={name} label={label} {...props} disabled={disabled} />;

      case 'radio':
        return <RhfMuiRadioGroup name={name} label={label} disabled={disabled} options={props.options || []} />;

      case 'select':
        // Special handling for charge selection
        if (domainType === 'SELECT_A_CHARGE_NAME') {
          return <RhfFieldSelectSubChargeDomain name={name} disabled={disabled} answerId={props.answerId || ''} />;
        }
        return <RhfMuiSelect name={name} label={label} {...props} disabled={disabled} />;

      case 'multiselect':
        return <RhfMuiSelect name={name} label={label} multiple {...props} disabled={disabled} />;

      case 'date':
        return <RhfMuiDatePicker name={name} label={label} {...props} />;

      case 'datetime':
        return <RhfMuiDateTimePicker name={name} label={label} {...props} />;

      case 'daterange':
        return <RhfMuiDateRangePicker name={name} label={label} {...props} disabled={disabled} />;

      default:
        console.warn(`Unsupported field type: ${type}`);
        return <RhfMuiTextField name={name} label={label} {...props} disabled={disabled} />;
    }
  };

  // Handle special case for DO_YOU_ACCEPT_THIS_NEW_CHARGE_AS_IT
  if (isNewCharge) {
    const baseField = renderBaseField();
    const noticeEl = <ActionDetailNotice key='notice' />;
    const customInputs = <LegAndDomainAssignment disabled={disabled} deviationId={deviationId} questionFieldName={name} />;

    // Clone the base field to add custom styling and children
    return React.cloneElement(
      baseField,
      {
        ...baseField.props,
        labelProps: {
          border: 'none',
        },
      },
      setupCustomInputs ? setupCustomInputs(noticeEl, customInputs) : [noticeEl, customInputs]
    );
  }

  // Handle special case for DO_YOU_ACCEPT_THIS_DOUBLE_CHARGE
  if (isDoubleCharge && setupCustomInputs) {
    const baseField = renderBaseField();
    const noticeEl = null;
    const customInputs = null;

    return React.cloneElement(
      baseField,
      {
        ...baseField.props,
        labelProps: {
          border: 'none',
        },
      },
      setupCustomInputs(noticeEl, customInputs, disabled)
    );
  }

  // Return the base field for all other cases
  return renderBaseField();
};
